import { useState, useEffect } from 'react'
import { useQueryClient, QueryObserver } from '@tanstack/react-query'
import { IDatasetById } from '@global/api/transforms'

export const KEY = 'DATASET_DATA_BY_ID'

/**
 * Custom hook that observes dataset data changes from React Query's cache.
 *
 * @example
 * const { datasetDataById, data } = useDatasetsDataQueryObserver({ datasetId: '1234' });
 */
interface UseDatasetsDataQueryObserverProps {
  datasetId: number
}
interface UseDatasetsDataQueryObserverReturn {
  datasetDataById: IDatasetById
  data: IDatasetById[string] | null
}
export const useDatasetsDataQueryObserver = ({
  datasetId,
}: UseDatasetsDataQueryObserverProps): UseDatasetsDataQueryObserverReturn => {
  const queryClient = useQueryClient()

  /**
   * Initial state setup to fetch the collection of dataset data by IDs, from the query client.
   */
  const [datasetDataById, setDatasetDataById] = useState<IDatasetById>(() => {
    const data = queryClient.getQueryData<IDatasetById>([KEY])
    if (!data) return {}

    return data
  })

  useEffect(() => {
    const observer = new QueryObserver<IDatasetById>(queryClient, {
      queryKey: [KEY],
      queryFn: () => queryClient.getQueryData<IDatasetById>([KEY]) ?? {}, // Dummy query function
      staleTime: Infinity,
    })

    const unsubscribe = observer.subscribe(result => {
      if (!result.data) return {}

      setDatasetDataById(result.data)
    })

    return () => {
      unsubscribe()
    }
  }, [])

  const data: IDatasetById[string] | null = datasetDataById[String(datasetId)] ?? null

  return {
    datasetDataById,
    data,
  }
}
